import type {
  GraphMember,
  Relationship,
  Side,
} from '@/contexts/FamilyGraphContext';

import type {
  CanonicalTreeRelationshipEdge,
} from './CanonicalTreeRelationshipEdge';

import {
  buildCanonicalKinshipSemanticIndexes,
  deriveCanonicalKinshipSemantics,
  isCanonicalKinshipTerminalEdgeKind,
  canonicalKinshipProcessingGrammarIdentityForPath,
  canonicalKinshipSemanticRouteIdentityForPath,
  isCanonicalKinshipTraversalPathAdmissible,
  type CanonicalKinshipSemanticIndexes,
  type CanonicalKinshipSemantics,
} from './CanonicalTreeKinshipSemanticsAuthority';

export type CanonicalViewerBranch =
  | 'self'
  | 'maternal'
  | 'paternal'
  | 'both'
  | 'descendant'
  | 'partner-affiliated'
  | 'child-mediated'
  | 'affiliated'
  | 'unresolved';

export type CanonicalViewerConnectionKind =
  | 'self'
  | 'ancestor'
  | 'descendant'
  | 'sibling'
  | 'spouse-partner'
  | 'co-parent'
  | 'step-family'
  | 'in-law'
  | 'guardian'
  | 'foster-family'
  | 'chosen-family'
  | 'god-family'
  | 'affiliated-family'
  | 'unknown';

export type CanonicalViewerProjectionConfidence =
  | 'confirmed'
  | 'derived'
  | 'compatibility'
  | 'unresolved';

export interface CanonicalViewerConnectionStep {
  readonly fromMemberId: string;
  readonly toMemberId: string;
  readonly edgeId: string;
  readonly edgeKind:
    CanonicalTreeRelationshipEdge['kind'];
  readonly traversal:
    | 'forward'
    | 'reverse'
    | 'symmetric'
    | 'child-mediated';
}

export interface CanonicalViewerMemberProjection {
  readonly viewerId: string;
  readonly memberId: string;

  /**
   * Viewer-relative generation:
   * viewer = 0, ancestors positive, descendants negative.
   */
  readonly generation: number | null;

  readonly branch: CanonicalViewerBranch;
  readonly side: Side;

  readonly connectionKind:
    CanonicalViewerConnectionKind;

  /**
   * Canonical viewer-relative kinship.
   *
   * This is derived from the complete structural traversal shape, not
   * from the terminal edge and not from a stored display label.
   */
  readonly kinshipRelationship:
    Relationship;

  /**
   * Precise, additive canonical kinship semantics.
   *
   * The legacy kinshipRelationship field remains compatibility authority
   * until the separately governed presentation-convergence subcontract.
   */
  readonly kinshipSemantics:
    CanonicalKinshipSemantics;

  /**
   * Every retained bounded semantic-state branch contributing to viewer-relative relationship truth.
   *
   * These may represent structural, partnership-affinity, or
   * terminal-affiliation routes retained by the bounded semantic plane.
   */
  readonly authoritativeBranches:
    readonly CanonicalViewerBranch[];

  /**
   * Bounded semantic-state connection-path evidence for this canonical person.
   *
   * connectionPath below is the single canonical path selected by
   * kinshipSemantics.primary; this collection preserves the retained
   * semantic alternatives used to derive complete relationship truth.
   */
  readonly authoritativeConnectionPaths:
    readonly (
      readonly CanonicalViewerConnectionStep[]
    )[];

  readonly connectionPath:
    readonly CanonicalViewerConnectionStep[];

  readonly ancestryEdgeIds:
    readonly string[];

  readonly affiliationEdgeIds:
    readonly string[];

  readonly sourceEdgeIds:
    readonly string[];

  readonly confidence:
    CanonicalViewerProjectionConfidence;
}

export interface CanonicalViewerProjectionEvidence {
  readonly code: string;
  readonly memberIds: readonly string[];
  readonly edgeIds: readonly string[];
  readonly message: string;
}

export interface CanonicalViewerExplanatoryRelationship {
  readonly edgeId: string;
  readonly edgeKind:
    CanonicalTreeRelationshipEdge['kind'];
  readonly status:
    CanonicalTreeRelationshipEdge['status'];
  readonly fromMemberId: string;
  readonly toMemberId: string;
  readonly rendersActivePartnership: boolean;
  readonly connectionPath:
    readonly CanonicalViewerConnectionStep[];
}

export interface CanonicalTreeViewerProjectionResult {
  readonly viewerId: string;

  readonly projections:
    ReadonlyMap<
      string,
      CanonicalViewerMemberProjection
    >;

  readonly explanatoryRelationships:
    readonly CanonicalViewerExplanatoryRelationship[];

  readonly unresolvedEvidence:
    readonly CanonicalViewerProjectionEvidence[];

  readonly conflictingEvidence:
    readonly CanonicalViewerProjectionEvidence[];

  readonly unreachableMemberIds:
    readonly string[];
}

export interface CanonicalTreeViewerProjectionInput {
  readonly viewerId: string;
  readonly members: readonly GraphMember[];
  readonly edges:
    readonly CanonicalTreeRelationshipEdge[];
}

interface ProjectionTraversalState {
  readonly memberId: string;
  readonly generation: number;
  readonly branch: CanonicalViewerBranch;
  readonly side: Side;
  readonly connectionKind:
    CanonicalViewerConnectionKind;
  readonly connectionPath:
    readonly CanonicalViewerConnectionStep[];
  readonly ancestryEdgeIds: readonly string[];
  readonly affiliationEdgeIds: readonly string[];
  readonly sourceEdgeIds: readonly string[];
  readonly confidence:
    CanonicalViewerProjectionConfidence;
}

const activeStructuralStatuses =
  new Set<
    CanonicalTreeRelationshipEdge['status']
  >([
    'active',
    'not-applicable',
  ]);

const isActiveStructuralEdge = (
  edge: CanonicalTreeRelationshipEdge,
): boolean =>
  activeStructuralStatuses.has(edge.status) &&
  edge.confirmation !== 'rejected';

const unique = (
  values: readonly string[],
): readonly string[] =>
  Object.freeze(
    Array.from(new Set(values)),
  );

const freezeStep = (
  step: CanonicalViewerConnectionStep,
): CanonicalViewerConnectionStep =>
  Object.freeze({ ...step });

const freezeState = (
  state: ProjectionTraversalState,
): ProjectionTraversalState =>
  Object.freeze({
    ...state,
    connectionPath:
      Object.freeze(
        state.connectionPath.map(freezeStep),
      ),
    ancestryEdgeIds:
      unique(state.ancestryEdgeIds),
    affiliationEdgeIds:
      unique(state.affiliationEdgeIds),
    sourceEdgeIds:
      unique(state.sourceEdgeIds),
  });

interface CanonicalTraversalArc {
  readonly edge:
    CanonicalTreeRelationshipEdge;
  readonly fromMemberId: string;
  readonly toMemberId: string;
  readonly traversal:
    CanonicalViewerConnectionStep['traversal'];
  readonly generationDelta: number;
  readonly ancestry: boolean;
  readonly affiliation: boolean;
  readonly explanatoryOnly: boolean;
}

const sideToBranch = (
  side: Side,
): CanonicalViewerBranch => {
  if (side === 'mother') return 'maternal';
  if (side === 'father') return 'paternal';
  if (side === 'both') return 'both';
  if (side === 'self') return 'self';
  return 'unresolved';
};

const branchToSide = (
  branch: CanonicalViewerBranch,
): Side => {
  if (branch === 'maternal') return 'mother';
  if (branch === 'paternal') return 'father';
  if (branch === 'both') return 'both';
  return 'self';
};

const connectionKindFromEdge = (
  edge: CanonicalTreeRelationshipEdge,
  traversal:
    CanonicalViewerConnectionStep['traversal'],
): CanonicalViewerConnectionKind => {
  if (edge.kind === 'parent-child') {
    return traversal === 'forward'
      ? 'descendant'
      : 'ancestor';
  }

  return edge.kind;
};

const arcConfidence = (
  edge: CanonicalTreeRelationshipEdge,
): CanonicalViewerProjectionConfidence => {
  if (
    edge.confirmation === 'confirmed' &&
    edge.source === 'canonical-edge'
  ) {
    return 'confirmed';
  }

  if (
    edge.confirmation === 'confirmed' &&
    (
      edge.source === 'parent_ids' ||
      edge.source === 'spouse_partner_edges' ||
      edge.source === 'relationship_history'
    )
  ) {
    return 'derived';
  }

  if (edge.confirmation === 'rejected') {
    return 'unresolved';
  }

  return 'compatibility';
};

const buildTraversalArcs = (
  edges:
    readonly CanonicalTreeRelationshipEdge[],
): ReadonlyMap<
  string,
  readonly CanonicalTraversalArc[]
> => {
  const adjacency =
    new Map<string, CanonicalTraversalArc[]>();

  const addArc = (
    arc: CanonicalTraversalArc,
  ): void => {
    const list =
      adjacency.get(arc.fromMemberId) ?? [];

    list.push(Object.freeze(arc));
    adjacency.set(arc.fromMemberId, list);
  };

  for (const edge of edges) {
    if (
      edge.kind === 'parent-child' &&
      isActiveStructuralEdge(edge)
    ) {
      addArc({
        edge,
        fromMemberId:
          edge.sourceMemberId,
        toMemberId:
          edge.targetMemberId,
        traversal:
          'forward',
        generationDelta:
          -1,
        ancestry:
          true,
        affiliation:
          false,
        explanatoryOnly:
          false,
      });

      addArc({
        edge,
        fromMemberId:
          edge.targetMemberId,
        toMemberId:
          edge.sourceMemberId,
        traversal:
          'reverse',
        generationDelta:
          1,
        ancestry:
          true,
        affiliation:
          false,
        explanatoryOnly:
          false,
      });

      continue;
    }

    if (
      edge.kind === 'sibling' &&
      isActiveStructuralEdge(edge)
    ) {
      addArc({
        edge,
        fromMemberId:
          edge.sourceMemberId,
        toMemberId:
          edge.targetMemberId,
        traversal:
          'symmetric',
        generationDelta:
          0,
        ancestry:
          false,
        affiliation:
          false,
        explanatoryOnly:
          false,
      });

      addArc({
        edge,
        fromMemberId:
          edge.targetMemberId,
        toMemberId:
          edge.sourceMemberId,
        traversal:
          'symmetric',
        generationDelta:
          0,
        ancestry:
          false,
        affiliation:
          false,
        explanatoryOnly:
          false,
      });

      continue;
    }

    if (
      edge.kind === 'spouse-partner' &&
      edge.confirmation !== 'rejected'
    ) {
      const explanatoryOnly =
        edge.status !== 'active' ||
        !edge.rendersActivePartnership;

      addArc({
        edge,
        fromMemberId:
          edge.sourceMemberId,
        toMemberId:
          edge.targetMemberId,
        traversal:
          'symmetric',
        generationDelta:
          0,
        ancestry:
          false,
        affiliation:
          true,
        explanatoryOnly,
      });

      addArc({
        edge,
        fromMemberId:
          edge.targetMemberId,
        toMemberId:
          edge.sourceMemberId,
        traversal:
          'symmetric',
        generationDelta:
          0,
        ancestry:
          false,
        affiliation:
          true,
        explanatoryOnly,
      });

      continue;
    }

    if (
      edge.kind === 'co-parent' &&
      isActiveStructuralEdge(edge)
    ) {
      addArc({
        edge,
        fromMemberId:
          edge.sourceMemberId,
        toMemberId:
          edge.targetMemberId,
        traversal:
          'child-mediated',
        generationDelta:
          0,
        ancestry:
          false,
        affiliation:
          true,
        explanatoryOnly:
          false,
      });

      addArc({
        edge,
        fromMemberId:
          edge.targetMemberId,
        toMemberId:
          edge.sourceMemberId,
        traversal:
          'child-mediated',
        generationDelta:
          0,
        ancestry:
          false,
        affiliation:
          true,
        explanatoryOnly:
          false,
      });

      continue;
    }

    if (
      [
        'step-family',
        'in-law',
        'guardian',
        'foster-family',
        'chosen-family',
        'god-family',
        'affiliated-family',
      ].includes(edge.kind) &&
      edge.confirmation !== 'rejected'
    ) {
      addArc({
        edge,
        fromMemberId:
          edge.sourceMemberId,
        toMemberId:
          edge.targetMemberId,
        traversal:
          edge.direction === 'child-mediated'
            ? 'child-mediated'
            : edge.direction === 'symmetric'
              ? 'symmetric'
              : 'forward',
        generationDelta:
          0,
        ancestry:
          false,
        affiliation:
          true,
        explanatoryOnly:
          false,
      });

      addArc({
        edge,
        fromMemberId:
          edge.targetMemberId,
        toMemberId:
          edge.sourceMemberId,
        traversal:
          edge.direction === 'child-mediated'
            ? 'child-mediated'
            : edge.direction === 'symmetric'
              ? 'symmetric'
              : 'reverse',
        generationDelta:
          0,
        ancestry:
          false,
        affiliation:
          true,
        explanatoryOnly:
          false,
      });
    }
  }

  return new Map(
    Array.from(adjacency.entries()).map(
      ([memberId, arcs]) => [
        memberId,
        Object.freeze([...arcs]),
      ],
    ),
  );
};

interface CanonicalAncestryDagValidation {
  readonly hasCycle: boolean;
  readonly memberIds: readonly string[];
  readonly edgeIds: readonly string[];
}

const validateCanonicalAncestryDag = (
  members: readonly GraphMember[],
  edges:
    readonly CanonicalTreeRelationshipEdge[],
): CanonicalAncestryDagValidation => {
  const ancestryEdges =
    edges.filter(
      edge =>
        edge.kind === 'parent-child' &&
        edge.createsAncestry === true &&
        isActiveStructuralEdge(
          edge,
        ),
    );

  const nodeIds =
    new Set<string>(
      members.map(
        member =>
          member.id,
      ),
    );

  const indegree =
    new Map<string, number>();

  const adjacency =
    new Map<string, string[]>();

  for (const id of nodeIds) {
    indegree.set(
      id,
      0,
    );
  }

  for (const edge of ancestryEdges) {
    nodeIds.add(
      edge.sourceMemberId,
    );

    nodeIds.add(
      edge.targetMemberId,
    );

    if (
      !indegree.has(
        edge.sourceMemberId,
      )
    ) {
      indegree.set(
        edge.sourceMemberId,
        0,
      );
    }

    if (
      !indegree.has(
        edge.targetMemberId,
      )
    ) {
      indegree.set(
        edge.targetMemberId,
        0,
      );
    }

    const children =
      adjacency.get(
        edge.sourceMemberId,
      ) ?? [];

    children.push(
      edge.targetMemberId,
    );

    adjacency.set(
      edge.sourceMemberId,
      children,
    );

    indegree.set(
      edge.targetMemberId,
      (
        indegree.get(
          edge.targetMemberId,
        ) ?? 0
      ) + 1,
    );
  }

  for (
    const children of
      adjacency.values()
  ) {
    children.sort();
  }

  const ready =
    Array.from(
      nodeIds,
    )
      .filter(
        id =>
          (
            indegree.get(
              id,
            ) ?? 0
          ) === 0,
      )
      .sort();

  let cursor = 0;
  let visited = 0;

  while (
    cursor < ready.length
  ) {
    const id =
      ready[cursor++];

    visited += 1;

    for (
      const child of
        adjacency.get(
          id,
        ) ?? []
    ) {
      const next =
        (
          indegree.get(
            child,
          ) ?? 0
        ) - 1;

      indegree.set(
        child,
        next,
      );

      if (next === 0) {
        ready.push(
          child,
        );
      }
    }
  }

  if (
    visited ===
    nodeIds.size
  ) {
    return Object.freeze({
      hasCycle: false,
      memberIds:
        Object.freeze([]),
      edgeIds:
        Object.freeze([]),
    });
  }

  const remaining =
    Array.from(
      nodeIds,
    )
      .filter(
        id =>
          (
            indegree.get(
              id,
            ) ?? 0
          ) > 0,
      )
      .sort();

  const remainingSet =
    new Set(
      remaining,
    );

  return Object.freeze({
    hasCycle: true,
    memberIds:
      Object.freeze(
        remaining,
      ),
    edgeIds:
      Object.freeze(
        ancestryEdges
          .filter(
            edge =>
              remainingSet.has(
                edge.sourceMemberId,
              ) &&
              remainingSet.has(
                edge.targetMemberId,
              ),
          )
          .map(
            edge =>
              edge.id,
          )
          .sort(),
      ),
  });
};

const branchForParentTraversal = (
  current: ProjectionTraversalState,
  target: GraphMember,
): CanonicalViewerBranch => {
  /*
   * Once ancestry has entered a governed maternal, paternal, or both-side
   * branch, every higher ancestor remains inside that same recursive branch.
   */
  if (
    current.connectionPath.length > 0 &&
    (
      current.branch === 'maternal' ||
      current.branch === 'paternal' ||
      current.branch === 'both'
    )
  ) {
    return current.branch;
  }

  const storedBranch =
    sideToBranch(target.side);

  if (
    storedBranch === 'maternal' ||
    storedBranch === 'paternal' ||
    storedBranch === 'both'
  ) {
    return storedBranch;
  }

  return current.branch === 'self'
    ? 'unresolved'
    : current.branch;
};

const branchForArc = (
  current: ProjectionTraversalState,
  arc: CanonicalTraversalArc,
  target: GraphMember,
): CanonicalViewerBranch => {
  if (arc.edge.kind === 'parent-child') {
    if (arc.traversal === 'reverse') {
      return branchForParentTraversal(
        current,
        target,
      );
    }

    if (
      current.branch === 'maternal' ||
      current.branch === 'paternal' ||
      current.branch === 'both'
    ) {
      return current.branch;
    }

    return 'descendant';
  }

  if (arc.edge.kind === 'sibling') {
    return current.branch;
  }

  if (arc.edge.kind === 'spouse-partner') {
    if (
      current.branch === 'maternal' ||
      current.branch === 'paternal' ||
      current.branch === 'both' ||
      current.branch === 'descendant'
    ) {
      return current.branch;
    }

    return 'partner-affiliated';
  }

  if (arc.edge.kind === 'co-parent') {
    return 'child-mediated';
  }

  if (
    arc.edge.affiliatedBranch ||
    [
      'chosen-family',
      'god-family',
      'guardian',
      'foster-family',
      'affiliated-family',
    ].includes(arc.edge.kind)
  ) {
    return 'affiliated';
  }

  if (
    arc.edge.kind === 'step-family' ||
    arc.edge.kind === 'in-law'
  ) {
    return current.branch === 'self'
      ? 'partner-affiliated'
      : current.branch;
  }

  return 'unresolved';
};

const statePriority = (
  state: ProjectionTraversalState,
): readonly number[] => [
  state.connectionPath.length,
  state.confidence === 'confirmed'
    ? 0
    : state.confidence === 'derived'
      ? 1
      : state.confidence === 'compatibility'
        ? 2
        : 3,
  state.branch === 'unresolved' ? 1 : 0,
];

const compareStatePriority = (
  leftState: ProjectionTraversalState,
  rightState: ProjectionTraversalState,
): number => {
  const left =
    statePriority(leftState);

  const right =
    statePriority(rightState);

  for (
    let index = 0;
    index < left.length;
    index += 1
  ) {
    if (left[index] < right[index]) {
      return -1;
    }

    if (left[index] > right[index]) {
      return 1;
    }
  }

  return 0;
};

type CanonicalTraversalAuthorityClass =
  | 'structural'
  | 'partnership-affinity'
  | 'terminal-affiliation';

const traversalAuthorityClassForState = (
  state: ProjectionTraversalState,
): CanonicalTraversalAuthorityClass => {
  if (
    state.connectionPath.some(
      step =>
        isCanonicalKinshipTerminalEdgeKind(
          step.edgeKind,
        ),
    )
  ) {
    return 'terminal-affiliation';
  }

  if (
    state.connectionPath.some(
      step =>
        step.edgeKind ===
        'spouse-partner',
    )
  ) {
    return 'partnership-affinity';
  }

  return 'structural';
};

const semanticRetentionKey = (
  state: ProjectionTraversalState,
): string =>
  JSON.stringify([
    state.branch,
    state.generation,
    canonicalKinshipSemanticRouteIdentityForPath(
      state.connectionPath,
    ),
  ]);

const canonicalProcessingStateKey = (
  _viewerId: string,
  state: ProjectionTraversalState,
): string =>
  JSON.stringify([
    state.memberId,
    state.branch,
    state.generation,
    canonicalKinshipProcessingGrammarIdentityForPath(
      state.connectionPath,
    ),
  ]);

const pathAlreadyContainsMember = (
  viewerId: string,
  state: ProjectionTraversalState,
  memberId: string,
): boolean =>
  memberId === viewerId ||
  state.connectionPath.some(
    step =>
      step.toMemberId === memberId,
  );

const canonicalBranchRank = (
  branch: CanonicalViewerBranch,
): number => {
  switch (branch) {
    case 'self':
      return 0;
    case 'maternal':
      return 1;
    case 'paternal':
      return 2;
    case 'both':
      return 3;
    case 'descendant':
      return 4;
    case 'child-mediated':
      return 5;
    case 'partner-affiliated':
      return 6;
    case 'affiliated':
      return 7;
    case 'unresolved':
    default:
      return 8;
  }
};

const sortedUnique = (
  values: readonly string[],
): readonly string[] =>
  Object.freeze(
    Array.from(
      new Set(values),
    ).sort(),
  );

const canonicalConnectionPathKey = (
  path:
    readonly CanonicalViewerConnectionStep[],
): string =>
  JSON.stringify(
    path.map(
      step => ({
        edgeId:
          step.edgeId,
        edgeKind:
          step.edgeKind,
        traversal:
          step.traversal,
        from:
          step.fromMemberId,
        to:
          step.toMemberId,
      }),
    ),
  );

const canonicalStateEvidenceKey = (
  state: ProjectionTraversalState,
): string =>
  JSON.stringify({
    generation:
      state.generation,
    branch:
      state.branch,
    connectionKind:
      state.connectionKind,
    path:
      state.connectionPath.map(
        step => ({
          edgeId:
            step.edgeId,
          edgeKind:
            step.edgeKind,
          traversal:
            step.traversal,
          from:
            step.fromMemberId,
          to:
            step.toMemberId,
        }),
      ),
  });

const compareCanonicalEvidenceState = (
  left: ProjectionTraversalState,
  right: ProjectionTraversalState,
): number => {
  const branchDifference =
    canonicalBranchRank(left.branch) -
    canonicalBranchRank(right.branch);

  if (branchDifference !== 0) {
    return branchDifference;
  }

  const leftShape =
    left.connectionPath
      .map(
        step =>
          `${step.edgeKind}:${step.traversal}`,
      )
      .join('|');

  const rightShape =
    right.connectionPath
      .map(
        step =>
          `${step.edgeKind}:${step.traversal}`,
      )
      .join('|');

  const shapeDifference =
    leftShape.localeCompare(
      rightShape,
    );

  if (shapeDifference !== 0) {
    return shapeDifference;
  }

  /*
   * Stable evidence ordering only.
   *
   * Edge identity is never branch, generation, kinship, or geometry
   * authority. This final comparison only makes the explanatory evidence
   * array reproducible after all structural decisions are already made.
   */
  return canonicalStateEvidenceKey(left)
    .localeCompare(
      canonicalStateEvidenceKey(right),
    );
};

const compactEquivalentAuthoritativeState = (
  states:
    readonly ProjectionTraversalState[],
  candidate:
    ProjectionTraversalState,
): readonly ProjectionTraversalState[] => {
  const sameBranchIndex =
    states.findIndex(
      state =>
        state.branch ===
        candidate.branch,
    );

  if (sameBranchIndex < 0) {
    return Object.freeze([
      ...states,
      candidate,
    ]);
  }

  const existing =
    states[sameBranchIndex];

  /*
   * One deterministic shortest-path representative is retained for each
   * canonical branch. Branch multiplicity remains topology authority;
   * path multiplicity inside the same branch does not.
   */
  if (
    compareCanonicalEvidenceState(
      candidate,
      existing,
    ) >= 0
  ) {
    return Object.freeze(
      [...states],
    );
  }

  const next =
    [...states];

  next[sameBranchIndex] =
    candidate;

  return Object.freeze(next);
};

const authoritativeBranchesFor = (
  states:
    readonly ProjectionTraversalState[],
): readonly CanonicalViewerBranch[] =>
  Object.freeze(
    Array.from(
      new Set(
        states.map(
          state => state.branch,
        ),
      ),
    ).sort(
      (left, right) =>
        canonicalBranchRank(left) -
        canonicalBranchRank(right),
    ),
  );

const resolveAuthoritativeBranch = (
  states:
    readonly ProjectionTraversalState[],
): CanonicalViewerBranch => {
  const branches =
    authoritativeBranchesFor(
      states,
    );

  if (branches.length === 1) {
    return branches[0];
  }

  const structuralBranches =
    branches.filter(
      branch =>
        branch === 'maternal' ||
        branch === 'paternal' ||
        branch === 'both',
    );

  if (
    structuralBranches.length ===
      branches.length &&
    (
      structuralBranches.includes(
        'both',
      ) ||
      (
        structuralBranches.includes(
          'maternal',
        ) &&
        structuralBranches.includes(
          'paternal',
        )
      )
    )
  ) {
    return 'both';
  }

  /*
   * Incompatible equally authoritative branch classes cannot be guessed.
   */
  return 'unresolved';
};

const kinshipRelationshipForState = (
  state: ProjectionTraversalState,
  resolvedBranch:
    CanonicalViewerBranch,
): Relationship => {
  if (
    state.connectionKind ===
    'spouse-partner'
  ) {
    return 'spouse';
  }

  if (
    state.connectionKind ===
    'sibling'
  ) {
    return 'sibling';
  }

  if (
    [
      'co-parent',
      'step-family',
      'in-law',
      'guardian',
      'foster-family',
      'chosen-family',
      'god-family',
      'affiliated-family',
      'unknown',
    ].includes(
      state.connectionKind,
    )
  ) {
    return 'unknown';
  }

  const steps =
    state.connectionPath;

  if (steps.length === 0) {
    return 'unknown';
  }

  /*
   * Explicit sibling evidence may be followed by one or more downward
   * parent-child edges for nieces/nephews and their descendants.
   */
  if (
    steps[0]?.edgeKind ===
      'sibling' &&
    steps
      .slice(1)
      .every(
        step =>
          step.edgeKind ===
            'parent-child' &&
          step.traversal ===
            'forward',
      )
  ) {
    return steps.length === 1
      ? 'sibling'
      : 'cousin-child';
  }

  if (
    !steps.every(
      step =>
        step.edgeKind ===
        'parent-child',
    )
  ) {
    return 'unknown';
  }

  let seenForward = false;
  let up = 0;
  let down = 0;

  for (const step of steps) {
    if (step.traversal === 'reverse') {
      if (seenForward) {
        return 'unknown';
      }

      up += 1;
      continue;
    }

    if (step.traversal === 'forward') {
      seenForward = true;
      down += 1;
      continue;
    }

    return 'unknown';
  }

  if (up > 0 && down === 0) {
    if (up === 1) {
      if (
        resolvedBranch ===
        'maternal'
      ) {
        return 'mother';
      }

      if (
        resolvedBranch ===
        'paternal'
      ) {
        return 'father';
      }

      return 'unknown';
    }

    if (up === 2) {
      return 'grandparent';
    }

    return 'great-grandparent';
  }

  if (up === 0 && down > 0) {
    return 'child';
  }

  if (up === 1 && down === 1) {
    return 'sibling';
  }

  if (up >= 2 && down === 1) {
    return 'aunt-uncle';
  }

  if (up === 1 && down >= 2) {
    return 'cousin-child';
  }

  if (up >= 2 && down >= 2) {
    return down > up
      ? 'cousin-child'
      : 'cousin';
  }

  return 'unknown';
};

const toMemberProjection = (
  viewerId: string,
  states:
    readonly ProjectionTraversalState[],
  semanticStates:
    readonly ProjectionTraversalState[],
  semanticIndexes:
    CanonicalKinshipSemanticIndexes,
): CanonicalViewerMemberProjection => {
  const orderedStates =
    [...states].sort(
      compareCanonicalEvidenceState,
    );

  const representative =
    orderedStates[0];

  const resolvedBranch =
    resolveAuthoritativeBranch(
      orderedStates,
    );

  const orderedSemanticStates =
    [...semanticStates].sort(
      compareCanonicalEvidenceState,
    );

  const kinshipSemantics =
    deriveCanonicalKinshipSemantics({
      viewerId,
      memberId:
        representative.memberId,
      connectionPaths:
        orderedSemanticStates.map(
          state =>
            state.connectionPath,
        ),
      indexes:
        semanticIndexes,
    });

  const semanticPrimaryPathKey =
    canonicalConnectionPathKey(
      kinshipSemantics
        .primary.connectionPath,
    );

  const canonicalRepresentative =
    orderedSemanticStates.find(
      state =>
        canonicalConnectionPathKey(
          state.connectionPath,
        ) ===
          semanticPrimaryPathKey,
    ) ??
    representative;

  const canonicalResolvedBranch =
    canonicalRepresentative.branch;

  return Object.freeze({
    viewerId,
    memberId:
      canonicalRepresentative.memberId,
    generation:
      canonicalRepresentative.generation,
    branch:
      canonicalResolvedBranch,
    side:
      branchToSide(
        canonicalResolvedBranch,
      ),
    connectionKind:
      canonicalRepresentative.connectionKind,
    kinshipRelationship:
      kinshipRelationshipForState(
        representative,
        resolvedBranch,
      ),
    kinshipSemantics,
    authoritativeBranches:
      authoritativeBranchesFor(
        orderedSemanticStates,
      ),
    authoritativeConnectionPaths:
      Object.freeze(
        orderedSemanticStates.map(
          state =>
            Object.freeze(
              state.connectionPath.map(
                freezeStep,
              ),
            ),
        ),
      ),
    connectionPath:
      Object.freeze(
        canonicalRepresentative
          .connectionPath
          .map(freezeStep),
      ),
    ancestryEdgeIds:
      sortedUnique(
        canonicalRepresentative
          .ancestryEdgeIds,
      ),
    affiliationEdgeIds:
      sortedUnique(
        canonicalRepresentative
          .affiliationEdgeIds,
      ),
    sourceEdgeIds:
      sortedUnique(
        canonicalRepresentative
          .sourceEdgeIds,
      ),
    confidence:
      canonicalRepresentative.confidence,
  });
};


export const projectCanonicalTreeForViewer = (
  input: CanonicalTreeViewerProjectionInput,
): CanonicalTreeViewerProjectionResult => {
  const members =
    [...input.members];

  const memberById =
    new Map(
      members.map(
        member =>
          [member.id, member] as const,
      ),
    );

  const viewer =
    memberById.get(input.viewerId);

  const unresolvedEvidence:
    CanonicalViewerProjectionEvidence[] = [];

  const conflictingEvidence:
    CanonicalViewerProjectionEvidence[] = [];

  if (!viewer) {
    return Object.freeze({
      viewerId:
        input.viewerId,
      projections:
        new Map(),
      explanatoryRelationships:
        Object.freeze([]),
      unresolvedEvidence:
        Object.freeze([
          Object.freeze({
            code:
              'VIEWER-NOT-FOUND',
            memberIds:
              Object.freeze([
                input.viewerId,
              ]),
            edgeIds:
              Object.freeze([]),
            message:
              'Viewer member is not present in the loaded graph.',
          }),
        ]),
      conflictingEvidence:
        Object.freeze([]),
      unreachableMemberIds:
        Object.freeze(
          members.map(
            member => member.id,
          ),
        ),
    });
  }

  const arcsByMember =
    buildTraversalArcs(input.edges);

  /*
   * Build canonical semantic indexes once for this projection.
   * Derived kinship is not persisted and does not perform per-member
   * full-edge rescans.
   */
  const semanticIndexes =
    buildCanonicalKinshipSemanticIndexes(
      input.edges,
    );

  const initialState =
    freezeState({
      memberId:
        input.viewerId,
      generation:
        0,
      branch:
        'self',
      side:
        'self',
      connectionKind:
        'self',
      connectionPath:
        [],
      ancestryEdgeIds:
        [],
      affiliationEdgeIds:
        [],
      sourceEdgeIds:
        [],
      confidence:
        'confirmed',
    });

  const ancestryDagValidation =
    validateCanonicalAncestryDag(
      members,
      input.edges,
    );

  if (
    ancestryDagValidation.hasCycle
  ) {
    unresolvedEvidence.push(
      Object.freeze({
        code:
          'ANCESTRY-CYCLE',
        memberIds:
          ancestryDagValidation.memberIds,
        edgeIds:
          ancestryDagValidation.edgeIds,
        message:
          'Active canonical ancestry contains a cycle; viewer projection failed closed.',
      }),
    );
  }

  const buildTraversalCandidate = (
    current:
      ProjectionTraversalState,
    arc:
      CanonicalTraversalArc,
    target:
      GraphMember,
  ): ProjectionTraversalState => {
    const branch =
      branchForArc(
        current,
        arc,
        target,
      );

    const step =
      freezeStep({
        fromMemberId:
          arc.fromMemberId,
        toMemberId:
          arc.toMemberId,
        edgeId:
          arc.edge.id,
        edgeKind:
          arc.edge.kind,
        traversal:
          arc.traversal,
      });

    return freezeState({
      memberId:
        target.id,
      generation:
        current.generation +
        arc.generationDelta,
      branch,
      side:
        branchToSide(
          branch,
        ),
      connectionKind:
        connectionKindFromEdge(
          arc.edge,
          arc.traversal,
        ),
      connectionPath:
        [
          ...current.connectionPath,
          step,
        ],
      ancestryEdgeIds:
        arc.ancestry
          ? [
              ...current.ancestryEdgeIds,
              arc.edge.id,
            ]
          : current.ancestryEdgeIds,
      affiliationEdgeIds:
        arc.affiliation
          ? [
              ...current.affiliationEdgeIds,
              arc.edge.id,
            ]
          : current.affiliationEdgeIds,
      sourceEdgeIds:
        [
          ...current.sourceEdgeIds,
          arc.edge.id,
        ],
      confidence:
        arcConfidence(
          arc.edge,
        ),
    });
  };

  const bestStateByMember =
    new Map<
      string,
      ProjectionTraversalState
    >([
      [
        input.viewerId,
        initialState,
      ],
    ]);

  const authoritativeStatesByMember =
    new Map<
      string,
      ProjectionTraversalState[]
    >([
      [
        input.viewerId,
        [
          initialState,
        ],
      ],
    ]);


  /*
   * Legacy projection authority remains in bestStateByMember and
   * authoritativeStatesByMember.
   *
   * This separate semantic-retention plane preserves independently valid
   * viewer-relative truths. Its identity is precise semantic route +
   * canonical branch + generation, never raw evidence-path multiplicity.
   *
   * No constant states-per-member correctness bound is asserted.
   */
  const traversalAuthorityStatesByMember =
    new Map<
      string,
      Map<
        string,
        ProjectionTraversalState
      >
    >([
      [
        input.viewerId,
        new Map([
          [
            semanticRetentionKey(
              initialState,
            ),
            initialState,
          ],
        ]),
      ],
    ]);

  const initialProcessingKey =
    canonicalProcessingStateKey(
      input.viewerId,
      initialState,
    );

  const processingStateByKey =
    new Map<
      string,
      ProjectionTraversalState
    >([
      [
        initialProcessingKey,
        initialState,
      ],
    ]);

  const queuedProcessingStateKeys =
    new Set<string>(
      ancestryDagValidation.hasCycle
        ? []
        : [
            initialProcessingKey,
          ],
    );

  const processedProcessingStateKeys =
    new Set<string>();

  type AlternateSimpleWitnessResult =
    | Readonly<{
        status: 'found';
        state:
          ProjectionTraversalState;
      }>
    | Readonly<{
        status: 'not-found';
      }>
    | Readonly<{
        status: 'indeterminate';
      }>;

  const witnessSearchCache =
    new Map<
      string,
      AlternateSimpleWitnessResult
    >();

  const reportedWitnessIndeterminateKeys =
    new Set<string>();

  const witnessExpansionBudget =
    Math.min(
      250_000,
      Math.max(
        4_096,
        members.length *
          Math.max(
            1,
            input.edges.length,
          ) *
          32,
      ),
    );

  let witnessExpansionCount = 0;

  const canonicalWitnessArcKey = (
    arc:
      CanonicalTraversalArc,
  ): string =>
    JSON.stringify([
      arc.edge.kind,
      arc.traversal,
      arc.edge.id,
      arc.fromMemberId,
      arc.toMemberId,
    ]);

  const witnessEdgeById =
    new Map(
      input.edges.map(
        edge =>
          [
            edge.id,
            edge,
          ] as const,
      ),
    );

  const isExactReverseAncestryWitnessState = (
    state:
      ProjectionTraversalState,
  ): boolean =>
    state.connectionPath.length > 0 &&
    state.connectionPath.every(
      step => {
        if (
          step.edgeKind !==
            'parent-child' ||
          step.traversal !==
            'reverse'
        ) {
          return false;
        }

        const edge =
          witnessEdgeById.get(
            step.edgeId,
          );

        return Boolean(
          edge &&
          edge.kind ===
            'parent-child' &&
          edge.createsAncestry ===
            true &&
          isActiveStructuralEdge(
            edge,
          ),
        );
      },
    );

  const resolveExactReverseAncestryWitness = (
    requestedState:
      ProjectionTraversalState,
    forbiddenTargetId: string,
  ): AlternateSimpleWitnessResult => {
    if (
      forbiddenTargetId ===
        input.viewerId ||
      forbiddenTargetId ===
        requestedState.memberId
    ) {
      return Object.freeze({
        status:
          'not-found' as const,
      });
    }

    const requestedKey =
      canonicalProcessingStateKey(
        input.viewerId,
        requestedState,
      );

    const requestedDepth =
      requestedState
        .connectionPath
        .length;

    let frontier =
      new Map<
        string,
        ProjectionTraversalState
      >([
        [
          canonicalProcessingStateKey(
            input.viewerId,
            initialState,
          ),
          initialState,
        ],
      ]);

    for (
      let depth = 0;
      depth < requestedDepth;
      depth += 1
    ) {
      const next =
        new Map<
          string,
          ProjectionTraversalState
        >();

      const states =
        [...frontier.entries()]
          .sort(
            ([left], [right]) =>
              left.localeCompare(
                right,
              ),
          )
          .map(
            ([, state]) =>
              state,
          );

      for (const state of states) {
        const arcs =
          [
            ...(
              arcsByMember.get(
                state.memberId,
              ) ?? []
            ),
          ]
            .filter(
              arc =>
                !arc.explanatoryOnly &&
                arc.edge.kind ===
                  'parent-child' &&
                arc.edge
                  .createsAncestry ===
                  true &&
                arc.traversal ===
                  'reverse',
            )
            .sort(
              (left, right) =>
                canonicalWitnessArcKey(
                  left,
                ).localeCompare(
                  canonicalWitnessArcKey(
                    right,
                  ),
                ),
            );

        for (const arc of arcs) {
          const target =
            memberById.get(
              arc.toMemberId,
            );

          if (!target) {
            continue;
          }

          if (
            target.id ===
            forbiddenTargetId
          ) {
            continue;
          }

          const candidate =
            buildTraversalCandidate(
              state,
              arc,
              target,
            );

          if (
            !isCanonicalKinshipTraversalPathAdmissible(
              candidate.connectionPath,
            )
          ) {
            continue;
          }

          const key =
            canonicalProcessingStateKey(
              input.viewerId,
              candidate,
            );

          if (
            !next.has(
              key,
            )
          ) {
            next.set(
              key,
              candidate,
            );
          }
        }
      }

      frontier = next;

      if (
        frontier.size === 0
      ) {
        break;
      }
    }

    const found =
      frontier.get(
        requestedKey,
      );

    return found
      ? Object.freeze({
          status:
            'found' as const,
          state:
            found,
        })
      : Object.freeze({
          status:
            'not-found' as const,
        });
  };

  interface CanonicalCommonPivotMixedShape {
    readonly up: number;
    readonly down: number;
  }

  const exactCommonPivotMixedShapeForState = (
    state:
      ProjectionTraversalState,
  ): CanonicalCommonPivotMixedShape | null => {
    let seenForward = false;
    let up = 0;
    let down = 0;

    for (
      const step of
        state.connectionPath
    ) {
      if (
        step.edgeKind !==
        'parent-child'
      ) {
        return null;
      }

      if (
        step.traversal ===
        'reverse'
      ) {
        if (seenForward) {
          return null;
        }

        up += 1;
        continue;
      }

      if (
        step.traversal ===
        'forward'
      ) {
        seenForward = true;
        down += 1;
        continue;
      }

      return null;
    }

    if (
      up <= 0 ||
      down <= 0
    ) {
      return null;
    }

    return Object.freeze({
      up,
      down,
    });
  };

  const ancestryTopologicalRank = (() => {
    const nodeIds =
      new Set<string>(
        members.map(
          member =>
            member.id,
        ),
      );

    const indegree =
      new Map<
        string,
        number
      >();

    const children =
      new Map<
        string,
        string[]
      >();

    for (const id of nodeIds) {
      indegree.set(
        id,
        0,
      );
    }

    for (
      const edge of
        input.edges
    ) {
      if (
        edge.kind !==
          'parent-child' ||
        edge.createsAncestry !==
          true ||
        !isActiveStructuralEdge(
          edge,
        )
      ) {
        continue;
      }

      nodeIds.add(
        edge.sourceMemberId,
      );

      nodeIds.add(
        edge.targetMemberId,
      );

      if (
        !indegree.has(
          edge.sourceMemberId,
        )
      ) {
        indegree.set(
          edge.sourceMemberId,
          0,
        );
      }

      if (
        !indegree.has(
          edge.targetMemberId,
        )
      ) {
        indegree.set(
          edge.targetMemberId,
          0,
        );
      }

      const list =
        children.get(
          edge.sourceMemberId,
        ) ?? [];

      list.push(
        edge.targetMemberId,
      );

      children.set(
        edge.sourceMemberId,
        list,
      );

      indegree.set(
        edge.targetMemberId,
        (
          indegree.get(
            edge.targetMemberId,
          ) ?? 0
        ) + 1,
      );
    }

    for (
      const list of
        children.values()
    ) {
      list.sort();
    }

    const ready =
      Array.from(
        nodeIds,
      )
        .filter(
          id =>
            (
              indegree.get(
                id,
              ) ?? 0
            ) === 0,
        )
        .sort();

    const order:
      string[] = [];

    while (
      ready.length > 0
    ) {
      ready.sort();

      const id =
        ready.shift();

      if (!id) {
        break;
      }

      order.push(
        id,
      );

      for (
        const child of
          children.get(
            id,
          ) ?? []
      ) {
        const next =
          (
            indegree.get(
              child,
            ) ?? 0
          ) - 1;

        indegree.set(
          child,
          next,
        );

        if (next === 0) {
          ready.push(
            child,
          );
        }
      }
    }

    return new Map(
      order.map(
        (id, index) =>
          [
            id,
            index,
          ] as const,
      ),
    );
  })();

  interface CanonicalCommonPivotProductState {
    readonly leftMemberId: string;
    readonly rightMemberId: string;
    readonly leftDepth: number;
    readonly rightDepth: number;
    readonly resolvedBranch:
      CanonicalViewerBranch | null;
  }

  interface CanonicalCommonPivotPredecessor {
    readonly previousKey: string;
    readonly coordinate:
      'left' | 'right';
    readonly arc:
      CanonicalTraversalArc;
  }

  const commonPivotProductKey = (
    state:
      CanonicalCommonPivotProductState,
  ): string =>
    JSON.stringify([
      state.leftMemberId,
      state.rightMemberId,
      state.leftDepth,
      state.rightDepth,
      state.resolvedBranch ?? '',
    ]);

  const commonPivotRank = (
    memberId: string,
  ): number =>
    ancestryTopologicalRank.get(
      memberId,
    ) ??
    Number.MAX_SAFE_INTEGER;

  const replayCommonPivotWitness = (
    leftArcs:
      readonly CanonicalTraversalArc[],
    rightArcs:
      readonly CanonicalTraversalArc[],
    requestedState:
      ProjectionTraversalState,
    forbiddenTargetId: string,
  ): ProjectionTraversalState | null => {
    let witness =
      initialState;

    for (
      const forwardArc of
        [...leftArcs].reverse()
    ) {
      const reverseArc =
        (
          arcsByMember.get(
            witness.memberId,
          ) ?? []
        ).find(
          arc =>
            arc.edge.id ===
              forwardArc.edge.id &&
            arc.traversal ===
              'reverse' &&
            arc.toMemberId ===
              forwardArc.fromMemberId,
        );

      if (!reverseArc) {
        return null;
      }

      const target =
        memberById.get(
          reverseArc.toMemberId,
        );

      if (!target) {
        return null;
      }

      if (
        target.id ===
        forbiddenTargetId
      ) {
        return null;
      }

      witness =
        buildTraversalCandidate(
          witness,
          reverseArc,
          target,
        );
    }

    for (
      const forwardArc of
        rightArcs
    ) {
      if (
        witness.memberId !==
        forwardArc.fromMemberId
      ) {
        return null;
      }

      const target =
        memberById.get(
          forwardArc.toMemberId,
        );

      if (!target) {
        return null;
      }

      if (
        target.id ===
        forbiddenTargetId
      ) {
        return null;
      }

      witness =
        buildTraversalCandidate(
          witness,
          forwardArc,
          target,
        );
    }

    const witnessMembers =
      [
        input.viewerId,
        ...witness.connectionPath.map(
          step =>
            step.toMemberId,
        ),
      ];

    if (
      new Set(
        witnessMembers,
      ).size !==
      witnessMembers.length
    ) {
      return null;
    }

    if (
      witnessMembers.includes(
        forbiddenTargetId,
      )
    ) {
      return null;
    }

    if (
      !isCanonicalKinshipTraversalPathAdmissible(
        witness.connectionPath,
      )
    ) {
      return null;
    }

    if (
      canonicalProcessingStateKey(
        input.viewerId,
        witness,
      ) !==
      canonicalProcessingStateKey(
        input.viewerId,
        requestedState,
      )
    ) {
      return null;
    }

    return witness;
  };

  const resolveExactCommonPivotMixedWitness = (
    requestedState:
      ProjectionTraversalState,
    forbiddenTargetId: string,
  ): AlternateSimpleWitnessResult => {
    const shape =
      exactCommonPivotMixedShapeForState(
        requestedState,
      );

    if (!shape) {
      return Object.freeze({
        status:
          'not-found' as const,
      });
    }

    if (
      forbiddenTargetId ===
        input.viewerId ||
      forbiddenTargetId ===
        requestedState.memberId
    ) {
      return Object.freeze({
        status:
          'not-found' as const,
      });
    }

    const requestedKey =
      canonicalProcessingStateKey(
        input.viewerId,
        requestedState,
      );

    const pivots =
      [...members]
        .filter(
          member =>
            member.id !==
              forbiddenTargetId &&
            member.id !==
              input.viewerId &&
            member.id !==
              requestedState.memberId,
        )
        .sort(
          (left, right) => {
            const rankDifference =
              commonPivotRank(
                left.id,
              ) -
              commonPivotRank(
                right.id,
              );

            return (
              rankDifference !== 0
                ? rankDifference
                : left.id.localeCompare(
                    right.id,
                  )
            );
          },
        );

    for (
      const pivot of pivots
    ) {
      const start:
        CanonicalCommonPivotProductState =
          Object.freeze({
            leftMemberId:
              pivot.id,
            rightMemberId:
              pivot.id,
            leftDepth:
              0,
            rightDepth:
              0,
            resolvedBranch:
              null,
          });

      const startKey =
        commonPivotProductKey(
          start,
        );

      const queue =
        [startKey];

      let cursor = 0;

      const states =
        new Map<
          string,
          CanonicalCommonPivotProductState
        >([
          [
            startKey,
            start,
          ],
        ]);

      const predecessors =
        new Map<
          string,
          CanonicalCommonPivotPredecessor
        >();

      let finalKey:
        string | null =
          null;

      while (
        cursor < queue.length
      ) {
        const key =
          queue[cursor];

        cursor += 1;

        const state =
          states.get(
            key,
          );

        if (!state) {
          continue;
        }

        const leftDone =
          state.leftMemberId ===
            input.viewerId &&
          state.leftDepth ===
            shape.up;

        const rightDone =
          state.rightMemberId ===
            requestedState.memberId &&
          state.rightDepth ===
            shape.down;

        const finalMixedBranch:
          CanonicalViewerBranch =
            (
              state.resolvedBranch ===
                'maternal' ||
              state.resolvedBranch ===
                'paternal' ||
              state.resolvedBranch ===
                'both'
            )
              ? state.resolvedBranch
              : 'descendant';

        if (
          leftDone &&
          rightDone &&
          finalMixedBranch ===
            requestedState.branch
        ) {
          finalKey = key;
          break;
        }

        if (
          state.leftDepth ===
            shape.up &&
          !leftDone
        ) {
          continue;
        }

        if (
          state.rightDepth ===
            shape.down &&
          !rightDone
        ) {
          continue;
        }

        const leftRank =
          leftDone
            ? Number.MAX_SAFE_INTEGER
            : commonPivotRank(
                state.leftMemberId,
              );

        const rightRank =
          rightDone
            ? Number.MAX_SAFE_INTEGER
            : commonPivotRank(
                state.rightMemberId,
              );

        const frontierRank =
          Math.min(
            leftRank,
            rightRank,
          );

        if (
          leftRank ===
          frontierRank
        ) {
          const arcs =
            [
              ...(
                arcsByMember.get(
                  state.leftMemberId,
                ) ?? []
              ),
            ]
              .filter(
                arc =>
                  !arc.explanatoryOnly &&
                  arc.edge.kind ===
                    'parent-child' &&
                  arc.edge
                    .createsAncestry ===
                    true &&
                  arc.traversal ===
                    'forward',
              )
              .sort(
                (left, right) =>
                  canonicalWitnessArcKey(
                    left,
                  ).localeCompare(
                    canonicalWitnessArcKey(
                      right,
                    ),
                  ),
              );

          for (const arc of arcs) {
            if (
              arc.toMemberId ===
                forbiddenTargetId ||
              arc.toMemberId ===
                state.rightMemberId
            ) {
              continue;
            }

            const nextDepth =
              state.leftDepth +
              1;

            if (
              nextDepth >
              shape.up
            ) {
              continue;
            }

            const currentLeftMember =
              memberById.get(
                state.leftMemberId,
              );

            if (!currentLeftMember) {
              continue;
            }

            const storedBranch =
              sideToBranch(
                currentLeftMember.side,
              );

            const nextBranch =
              (
                storedBranch ===
                  'maternal' ||
                storedBranch ===
                  'paternal' ||
                storedBranch ===
                  'both'
              )
                ? storedBranch
                : state.resolvedBranch;

            const next:
              CanonicalCommonPivotProductState =
                Object.freeze({
                  leftMemberId:
                    arc.toMemberId,
                  rightMemberId:
                    state.rightMemberId,
                  leftDepth:
                    nextDepth,
                  rightDepth:
                    state.rightDepth,
                  resolvedBranch:
                    nextBranch,
                });

            const nextKey =
              commonPivotProductKey(
                next,
              );

            if (
              states.has(
                nextKey,
              )
            ) {
              continue;
            }

            states.set(
              nextKey,
              next,
            );

            predecessors.set(
              nextKey,
              Object.freeze({
                previousKey:
                  key,
                coordinate:
                  'left',
                arc,
              }),
            );

            queue.push(
              nextKey,
            );
          }
        }

        if (
          rightRank ===
          frontierRank
        ) {
          const arcs =
            [
              ...(
                arcsByMember.get(
                  state.rightMemberId,
                ) ?? []
              ),
            ]
              .filter(
                arc =>
                  !arc.explanatoryOnly &&
                  arc.edge.kind ===
                    'parent-child' &&
                  arc.edge
                    .createsAncestry ===
                    true &&
                  arc.traversal ===
                    'forward',
              )
              .sort(
                (left, right) =>
                  canonicalWitnessArcKey(
                    left,
                  ).localeCompare(
                    canonicalWitnessArcKey(
                      right,
                    ),
                  ),
              );

          for (const arc of arcs) {
            if (
              arc.toMemberId ===
                forbiddenTargetId ||
              arc.toMemberId ===
                state.leftMemberId
            ) {
              continue;
            }

            const nextDepth =
              state.rightDepth +
              1;

            if (
              nextDepth >
              shape.down
            ) {
              continue;
            }

            const next:
              CanonicalCommonPivotProductState =
                Object.freeze({
                  leftMemberId:
                    state.leftMemberId,
                  rightMemberId:
                    arc.toMemberId,
                  leftDepth:
                    state.leftDepth,
                  rightDepth:
                    nextDepth,
                  resolvedBranch:
                    state.resolvedBranch,
                });

            const nextKey =
              commonPivotProductKey(
                next,
              );

            if (
              states.has(
                nextKey,
              )
            ) {
              continue;
            }

            states.set(
              nextKey,
              next,
            );

            predecessors.set(
              nextKey,
              Object.freeze({
                previousKey:
                  key,
                coordinate:
                  'right',
                arc,
              }),
            );

            queue.push(
              nextKey,
            );
          }
        }
      }

      if (!finalKey) {
        continue;
      }

      const leftArcs:
        CanonicalTraversalArc[] =
          [];

      const rightArcs:
        CanonicalTraversalArc[] =
          [];

      let traceKey =
        finalKey;

      while (
        traceKey !==
        startKey
      ) {
        const predecessor =
          predecessors.get(
            traceKey,
          );

        if (!predecessor) {
          leftArcs.length = 0;
          rightArcs.length = 0;
          break;
        }

        if (
          predecessor.coordinate ===
          'left'
        ) {
          leftArcs.push(
            predecessor.arc,
          );
        } else {
          rightArcs.push(
            predecessor.arc,
          );
        }

        traceKey =
          predecessor.previousKey;
      }

      leftArcs.reverse();
      rightArcs.reverse();

      if (
        leftArcs.length !==
          shape.up ||
        rightArcs.length !==
          shape.down
      ) {
        continue;
      }

      const witness =
        replayCommonPivotWitness(
          leftArcs,
          rightArcs,
          requestedState,
          forbiddenTargetId,
        );

      if (!witness) {
        continue;
      }

      if (
        canonicalProcessingStateKey(
          input.viewerId,
          witness,
        ) !==
        requestedKey
      ) {
        continue;
      }

      return Object.freeze({
        status:
          'found' as const,
        state:
          witness,
      });
    }

    return Object.freeze({
      status:
        'not-found' as const,
    });
  };

  const resolveAlternateSimpleWitness = (
    requestedState:
      ProjectionTraversalState,
    forbiddenTargetId: string,
  ): AlternateSimpleWitnessResult => {
    const requestedKey =
      canonicalProcessingStateKey(
        input.viewerId,
        requestedState,
      );

    const queryKey =
      JSON.stringify([
        requestedKey,
        forbiddenTargetId,
      ]);

    const cached =
      witnessSearchCache.get(
        queryKey,
      );

    if (cached) {
      return cached;
    }

    if (
      isExactReverseAncestryWitnessState(
        requestedState,
      )
    ) {
      const result =
        resolveExactReverseAncestryWitness(
          requestedState,
          forbiddenTargetId,
        );

      witnessSearchCache.set(
        queryKey,
        result,
      );

      return result;
    }

    if (
      forbiddenTargetId ===
        input.viewerId ||
      forbiddenTargetId ===
        requestedState.memberId
    ) {
      const result =
        Object.freeze({
          status:
            'not-found' as const,
        });

      witnessSearchCache.set(
        queryKey,
        result,
      );

      return result;
    }

    if (
      exactCommonPivotMixedShapeForState(
        requestedState,
      )
    ) {
      const result =
        resolveExactCommonPivotMixedWitness(
          requestedState,
          forbiddenTargetId,
        );

      witnessSearchCache.set(
        queryKey,
        result,
      );

      return result;
    }

    const grammar =
      requestedState
        .connectionPath
        .map(
          step =>
            Object.freeze({
              edgeKind:
                step.edgeKind,
              traversal:
                step.traversal,
            }),
        );

    let exhausted = false;

    const search = (
      state:
        ProjectionTraversalState,
      depth: number,
    ):
      ProjectionTraversalState |
      null => {
      if (
        depth ===
        grammar.length
      ) {
        return (
          canonicalProcessingStateKey(
            input.viewerId,
            state,
          ) === requestedKey
        )
          ? state
          : null;
      }

      const expected =
        grammar[depth];

      const arcs =
        [
          ...(
            arcsByMember.get(
              state.memberId,
            ) ?? []
          ),
        ]
          .filter(
            arc =>
              !arc.explanatoryOnly &&
              arc.edge.kind ===
                expected.edgeKind &&
              arc.traversal ===
                expected.traversal,
          )
          .sort(
            (left, right) =>
              canonicalWitnessArcKey(
                left,
              ).localeCompare(
                canonicalWitnessArcKey(
                  right,
                ),
              ),
          );

      for (const arc of arcs) {
        if (
          witnessExpansionCount >=
          witnessExpansionBudget
        ) {
          exhausted = true;
          return null;
        }

        const target =
          memberById.get(
            arc.toMemberId,
          );

        if (!target) {
          continue;
        }

        if (
          target.id ===
          forbiddenTargetId
        ) {
          continue;
        }

        if (
          pathAlreadyContainsMember(
            input.viewerId,
            state,
            target.id,
          )
        ) {
          continue;
        }

        witnessExpansionCount += 1;

        const candidate =
          buildTraversalCandidate(
            state,
            arc,
            target,
          );

        if (
          !isCanonicalKinshipTraversalPathAdmissible(
            candidate.connectionPath,
          )
        ) {
          continue;
        }

        const found =
          search(
            candidate,
            depth + 1,
          );

        if (found) {
          return found;
        }

        if (exhausted) {
          return null;
        }
      }

      return null;
    };

    const found =
      search(
        initialState,
        0,
      );

    const result:
      AlternateSimpleWitnessResult =
      found
        ? Object.freeze({
            status:
              'found' as const,
            state:
              found,
          })
        : exhausted
          ? Object.freeze({
              status:
                'indeterminate' as const,
            })
          : Object.freeze({
              status:
                'not-found' as const,
            });

    witnessSearchCache.set(
      queryKey,
      result,
    );

    return result;
  };

  const queue:
    string[] =
      ancestryDagValidation.hasCycle
        ? []
        : [
            initialProcessingKey,
          ];

  const enqueueTraversalState = (
    state: ProjectionTraversalState,
  ): void => {
    /*
     * Direct non-transitive affiliations are valid projected endpoints,
     * but the kinship grammar explicitly gives them no continuation
     * authority.
     */
    if (
      traversalAuthorityClassForState(
        state,
      ) === 'terminal-affiliation'
    ) {
      return;
    }

    const key =
      canonicalProcessingStateKey(
        input.viewerId,
        state,
      );

    if (
      processedProcessingStateKeys.has(
        key,
      )
    ) {
      return;
    }

    const existing =
      processingStateByKey.get(
        key,
      );

    if (
      !existing ||
      compareCanonicalEvidenceState(
        state,
        existing,
      ) < 0
    ) {
      processingStateByKey.set(
        key,
        state,
      );
    }

    if (
      queuedProcessingStateKeys.has(
        key,
      )
    ) {
      return;
    }

    queuedProcessingStateKeys.add(key);
    queue.push(key);
  };

  while (queue.length > 0) {
    const currentProcessingKey =
      queue.shift()!;

    queuedProcessingStateKeys.delete(
      currentProcessingKey,
    );

    if (
      processedProcessingStateKeys.has(
        currentProcessingKey,
      )
    ) {
      continue;
    }

    const current =
      processingStateByKey.get(
        currentProcessingKey,
      );

    if (!current) {
      continue;
    }

    processedProcessingStateKeys.add(
      currentProcessingKey,
    );

    const arcs =
      arcsByMember.get(
        current.memberId,
      ) ?? [];

    for (const arc of arcs) {
      if (arc.explanatoryOnly) {
        continue;
      }

      const target =
        memberById.get(
          arc.toMemberId,
        );

      if (!target) {
        unresolvedEvidence.push(
          Object.freeze({
            code:
              'EDGE-ENDPOINT-NOT-LOADED',
            memberIds:
              Object.freeze([
                arc.fromMemberId,
                arc.toMemberId,
              ]),
            edgeIds:
              Object.freeze([
                arc.edge.id,
              ]),
            message:
              'Canonical edge references a member outside the loaded viewer graph.',
          }),
        );

        continue;
      }

      /*
       * Stage 10G:
       *
       * Grammar impossibility is cheaper and more authoritative
       * than alternate simple-witness search. Never spend witness
       * authority on a continuation the semantic grammar already
       * rejects.
       */
      const representativeGrammarCandidate =
        buildTraversalCandidate(
          current,
          arc,
          target,
        );

      if (
        !isCanonicalKinshipTraversalPathAdmissible(
          representativeGrammarCandidate.connectionPath,
        )
      ) {
        continue;
      }

      let extensionBaseState =
        current;

      if (
        pathAlreadyContainsMember(
          input.viewerId,
          current,
          target.id,
        )
      ) {
        const witness =
          resolveAlternateSimpleWitness(
            current,
            target.id,
          );

        if (
          witness.status ===
          'indeterminate'
        ) {
          const queryIdentity =
            JSON.stringify([
              canonicalProcessingStateKey(
                input.viewerId,
                current,
              ),
              target.id,
            ]);

          if (
            !reportedWitnessIndeterminateKeys.has(
              queryIdentity,
            )
          ) {
            reportedWitnessIndeterminateKeys.add(
              queryIdentity,
            );

            unresolvedEvidence.push(
              Object.freeze({
                code:
                  'SIMPLE-WITNESS-INDETERMINATE',
                memberIds:
                  Object.freeze([
                    current.memberId,
                    target.id,
                  ]),
                edgeIds:
                  Object.freeze([]),
                message:
                  'Alternate simple-witness search exhausted its bounded projection budget; extension failed closed.',
              }),
            );
          }

          continue;
        }

        if (
          witness.status !==
          'found'
        ) {
          continue;
        }

        extensionBaseState =
          witness.state;
      }

      const candidate =
        buildTraversalCandidate(
          extensionBaseState,
          arc,
          target,
        );

      if (
        !isCanonicalKinshipTraversalPathAdmissible(
          candidate.connectionPath,
        )
      ) {
        continue;
      }

      const candidateAuthorityClass =
        traversalAuthorityClassForState(
          candidate,
        );

      const candidateSemanticRetentionKey =
        semanticRetentionKey(
          candidate,
        );

      const traversalAuthorityStates =
        traversalAuthorityStatesByMember.get(
          target.id,
        ) ??
        new Map<
          string,
          ProjectionTraversalState
        >();

      const existingTraversalAuthority =
        traversalAuthorityStates.get(
          candidateSemanticRetentionKey,
        );

      let retainTraversalAuthority =
        false;

      if (!existingTraversalAuthority) {
        retainTraversalAuthority =
          true;
      } else {
        const traversalPriority =
          compareStatePriority(
            candidate,
            existingTraversalAuthority,
          );

        if (traversalPriority < 0) {
          retainTraversalAuthority =
            true;
        } else if (
          traversalPriority === 0 &&
          candidate.generation ===
            existingTraversalAuthority.generation &&
          compareCanonicalEvidenceState(
            candidate,
            existingTraversalAuthority,
          ) < 0
        ) {
          /*
           * Deterministic replacement only. We intentionally retain one
           * canonical evidence representative per semantic truth identity,
           * while distinct precise truths remain independently retained.
           */
          retainTraversalAuthority =
            true;
        }
      }

      if (retainTraversalAuthority) {
        traversalAuthorityStates.set(
          candidateSemanticRetentionKey,
          candidate,
        );

        traversalAuthorityStatesByMember.set(
          target.id,
          traversalAuthorityStates,
        );

        enqueueTraversalState(
          candidate,
        );
      }

      const existing =
        bestStateByMember.get(
          target.id,
        );

      if (!existing) {
        bestStateByMember.set(
          target.id,
          candidate,
        );

        authoritativeStatesByMember.set(
          target.id,
          [
            candidate,
          ],
        );

        enqueueTraversalState(candidate);
        continue;
      }

      /*
       * Generation disagreement is a conflict only between comparable route
       * classes. A direct chosen-family/co-parent/other affiliation may
       * legitimately coexist with a structurally derived generation for the
       * same canonical person.
       */
      if (
        traversalAuthorityClassForState(
          existing,
        ) ===
          candidateAuthorityClass &&
        existing.generation !==
          candidate.generation
      ) {
        conflictingEvidence.push(
          Object.freeze({
            code:
              'GENERATION-CONFLICT',
            memberIds:
              Object.freeze([
                target.id,
              ]),
            edgeIds:
              unique([
                ...existing.sourceEdgeIds,
                ...candidate.sourceEdgeIds,
              ]),
            message:
              `Member ${target.id} is reachable at conflicting viewer-relative generations ${existing.generation} and ${candidate.generation}.`,
          }),
        );
      }

      const priorityComparison =
        compareStatePriority(
          candidate,
          existing,
        );

      if (priorityComparison < 0) {
        bestStateByMember.set(
          target.id,
          candidate,
        );

        authoritativeStatesByMember.set(
          target.id,
          [
            candidate,
          ],
        );

        enqueueTraversalState(candidate);
        continue;
      }

      if (
        priorityComparison === 0 &&
        existing.generation ===
          candidate.generation
      ) {
        const authoritativeStates =
          authoritativeStatesByMember.get(
            target.id,
          ) ?? [
            existing,
          ];

        const candidateKey =
          canonicalStateEvidenceKey(
            candidate,
          );

        const alreadyObserved =
          authoritativeStates.some(
            state =>
              canonicalStateEvidenceKey(
                state,
              ) === candidateKey,
          );

        if (!alreadyObserved) {
          const compacted =
            compactEquivalentAuthoritativeState(
              authoritativeStates,
              candidate,
            );

          authoritativeStatesByMember.set(
            target.id,
            [...compacted],
          );

          /*
           * Traversal propagation is independently governed by the bounded
           * class+branch authority plane. The legacy evidence array no longer
           * needs to retain every equivalent same-branch path.
           */
          enqueueTraversalState(candidate);
        }
      }
    }
  }

  const projections =
    new Map<
      string,
      CanonicalViewerMemberProjection
    >();

  for (
    const [
      memberId,
      state,
    ] of bestStateByMember
  ) {
    const authoritativeStates =
      authoritativeStatesByMember.get(
        memberId,
      ) ?? [
        state,
      ];

    const resolvedBranch =
      resolveAuthoritativeBranch(
        authoritativeStates,
      );

    const branchEvidence =
      authoritativeBranchesFor(
        authoritativeStates,
      );

    if (
      resolvedBranch ===
        'unresolved' &&
      branchEvidence.length > 1
    ) {
      conflictingEvidence.push(
        Object.freeze({
          code:
            'BRANCH-CONFLICT',
          memberIds:
            Object.freeze([
              memberId,
            ]),
          edgeIds:
            sortedUnique(
              authoritativeStates.flatMap(
                candidate =>
                  candidate.sourceEdgeIds,
              ),
            ),
          message:
            `Member ${memberId} has incompatible equally authoritative viewer branch evidence: ${branchEvidence.join(', ')}.`,
        }),
      );
    }

    const boundedTraversalStates =
      Array.from(
        traversalAuthorityStatesByMember
          .get(memberId)
          ?.values() ?? [],
      );

    const semanticStateByTruthKey =
      new Map<
        string,
        ProjectionTraversalState
      >();

    for (const semanticState of [
      ...authoritativeStates,
      ...boundedTraversalStates,
    ]) {
      const truthKey =
        semanticRetentionKey(
          semanticState,
        );

      const existingSemanticState =
        semanticStateByTruthKey.get(
          truthKey,
        );

      if (
        !existingSemanticState ||
        compareCanonicalEvidenceState(
          semanticState,
          existingSemanticState,
        ) < 0
      ) {
        semanticStateByTruthKey.set(
          truthKey,
          semanticState,
        );
      }
    }

    const semanticStates =
      Array.from(
        semanticStateByTruthKey.values(),
      );

    projections.set(
      memberId,
      toMemberProjection(
        input.viewerId,
        authoritativeStates,
        semanticStates,
        semanticIndexes,
      ),
    );
  }

  const explanatoryRelationships:
    CanonicalViewerExplanatoryRelationship[] = [];

  const seenExplanatoryEdges =
    new Set<string>();

  for (
    const [
      projectedMemberId,
      projectedState,
    ] of bestStateByMember
  ) {
    const arcs =
      arcsByMember.get(projectedMemberId) ?? [];

    for (const arc of arcs) {
      if (!arc.explanatoryOnly) continue;

      const identity = [
        arc.edge.id,
        arc.fromMemberId,
        arc.toMemberId,
      ].join('|');

      if (seenExplanatoryEdges.has(identity)) {
        continue;
      }

      seenExplanatoryEdges.add(identity);

      explanatoryRelationships.push(
        Object.freeze({
          edgeId:
            arc.edge.id,
          edgeKind:
            arc.edge.kind,
          status:
            arc.edge.status,
          fromMemberId:
            arc.fromMemberId,
          toMemberId:
            arc.toMemberId,
          rendersActivePartnership:
            arc.edge.rendersActivePartnership,
          connectionPath:
            Object.freeze([
              ...projectedState.connectionPath,
              freezeStep({
                fromMemberId:
                  arc.fromMemberId,
                toMemberId:
                  arc.toMemberId,
                edgeId:
                  arc.edge.id,
                edgeKind:
                  arc.edge.kind,
                traversal:
                  arc.traversal,
              }),
            ]),
        }),
      );
    }
  }

  const unreachableMemberIds =
    members
      .filter(
        member =>
          !projections.has(member.id),
      )
      .map(
        member => member.id,
      )
      .sort();

  return Object.freeze({
    viewerId:
      input.viewerId,
    projections:
      new Map(projections),
    explanatoryRelationships:
      Object.freeze(
        explanatoryRelationships,
      ),
    unresolvedEvidence:
      Object.freeze(
        unresolvedEvidence,
      ),
    conflictingEvidence:
      Object.freeze(
        conflictingEvidence,
      ),
    unreachableMemberIds:
      Object.freeze(
        unreachableMemberIds,
      ),
  });
};
